import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { TrendingDown } from "lucide-react";

export default function ClientWeight() {
  const [logs, setLogs] = useState([]);
  const [w, setW] = useState("");
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/app/client/weight-logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!w || isNaN(Number(w))) return toast.error("Enter a valid weight");
    setLoading(true);
    try {
      await api.post("/app/client/weight-logs", { weight_kg: Number(w) });
      setW("");
      toast.success("Weight logged");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const latest = logs[logs.length - 1];
  const first = logs[0];
  const delta = latest && first ? latest.weight_kg - first.weight_kg : 0;

  const max = Math.max(...logs.map((l) => l.weight_kg), 0) || 1;
  const min = Math.min(...logs.map((l) => l.weight_kg), max);
  const range = Math.max(max - min, 1);

  return (
    <div>
      <div className="section-eyebrow">Weight tracker</div>
      <h1 className="mt-1 font-display text-3xl leading-tight" style={{ color: "var(--primary)" }}>Your progress</h1>

      <form onSubmit={add} className="mt-6 card-soft p-4 flex gap-2">
        <Input data-testid="weight-input" type="number" step="0.1" placeholder="Today's weight in kg" value={w} onChange={(e) => setW(e.target.value)} className="h-12 rounded-xl" />
        <button data-testid="weight-add" disabled={loading} className="btn-primary text-sm disabled:opacity-60 px-6">Add</button>
      </form>

      {logs.length > 0 && (
        <div className="mt-6 card-soft p-5">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Current</div>
              <div className="font-display text-4xl" style={{ color: "var(--primary)" }}>{latest.weight_kg} <span className="text-lg">kg</span></div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Change</div>
              <div className="flex items-center gap-1 font-display text-2xl" style={{ color: delta < 0 ? "#2C633D" : "var(--red)" }}>
                <TrendingDown size={18} /> {delta > 0 ? "+" : ""}{delta.toFixed(1)} kg
              </div>
            </div>
          </div>
          <svg viewBox={`0 0 ${logs.length * 40 + 20} 120`} className="w-full mt-4" style={{ height: 120 }}>
            <polyline
              fill="none"
              stroke="var(--red)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={logs.map((l, i) => `${i * 40 + 20},${110 - ((l.weight_kg - min) / range) * 90}`).join(" ")}
            />
            {logs.map((l, i) => (
              <circle key={i} cx={i * 40 + 20} cy={110 - ((l.weight_kg - min) / range) * 90} r="5" fill="var(--yellow)" stroke="var(--primary)" strokeWidth="2" />
            ))}
          </svg>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {logs.slice().reverse().slice(0, 10).map((l) => (
          <div key={l.id} className="card-soft p-3 flex justify-between items-center">
            <span className="font-bold" style={{ color: "var(--primary)" }}>{l.weight_kg} kg</span>
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{new Date(l.date).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
